/**
 * The note that sits under a figure or table *on a slide*, in the APA house
 * style this app follows.
 *
 *   Note. <Title>. From <Author surname(s)>, <Year>, p. <page>. <Licence>
 *
 * This is not the reference-list entry and shares no formatter with it — see
 * `apaImageReference.js` for that. The differences are deliberate:
 *
 *   * "Note." is italic; everything after it is roman.
 *   * The title appears here in Title Case. (In a paper it sits on its own line
 *     above the note; on a slide it rides inside it.)
 *   * The author is the surname alone — "Perkins", never "Perkins, L.".
 *   * Only the year, never the month or day, even when the record stores one.
 *   * A Creative Commons licence or public-domain statement may appear, linked
 *     to its deed. A plain copyright line ("© 2008 Disney") never does.
 *   * No medium descriptor, no site or publisher, no URL, no figure number —
 *     all of those belong to the reference entry.
 *
 * The rules come from the college library's APA guidance for visuals and for
 * slide decks, which is narrower than generic APA 7 on both counts.
 */

import {
  cleanValue,
  contributorSurname,
  ensurePeriod,
  formatPageSegment,
  formatYearOnly,
  normalizeContributors,
  segmentsToHtml,
  segmentsToRuns,
  segmentsToText
} from './apaShared';
import { LICENSE_KIND, parseLicense } from './apaLicense';

export { segmentsToHtml, segmentsToRuns, segmentsToText };

/**
 * "Perkins", "Smith and Jones", "Smith et al." — the note's author.
 *
 * Not `formatInTextAuthor`: a parenthetical citation joins two names with "&",
 * a note reads them as words.
 */
export const formatNoteAuthor = (meta = {}) => {
  const names = normalizeContributors(meta).map(contributorSurname).filter(Boolean);
  if (!names.length) return '';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names[0]} et al.`;
};

// `page` is blanked by buildImageSourceMeta when the block's "include page"
// switch is off; a citation record carries the switch itself, so honour both.
const notePage = (meta = {}) => {
  if (meta.includePage === false) return '';
  return formatPageSegment(meta.page);
};

/**
 * The licence as it may appear on a slide, or null when the house style
 * excludes it. `includeLicense: false` hides it too, and a copyright string is
 * dropped silently — the editor is where the author is told why.
 */
export const noteLicense = (meta = {}) => {
  if (meta.includeLicense === false) return null;
  const parsed = parseLicense(meta.license || meta.copyright);
  if (parsed.kind !== LICENSE_KIND.CC && parsed.kind !== LICENSE_KIND.PUBLIC_DOMAIN) return null;
  return parsed;
};

const buildNote = (meta, { includeTitle }) => {
  const title = includeTitle ? cleanValue(meta.title) : '';
  const author = formatNoteAuthor(meta);
  const year = formatYearOnly(meta.date, meta.year);
  const pageSegment = notePage(meta);
  const prefix = meta.isAdapted ? 'Adapted from' : 'From';
  const license = noteLicense(meta);

  const segments = [{ text: 'Note.', italic: true }];
  if (title) segments.push({ text: ` ${title}.` });

  const fromParts = [author, year].filter(Boolean);
  if (pageSegment) fromParts.push(pageSegment);
  // ensurePeriod, so an undated source reads "From Perkins, n.d." rather than
  // closing a sentence that already ended.
  if (fromParts.length) segments.push({ text: ` ${prefix} ${ensurePeriod(fromParts.join(', '))}` });

  if (license) {
    segments.push({ text: ' ' });
    segments.push({ text: license.label, href: license.url || undefined });
    segments.push({ text: '.' });
  }

  const text = segmentsToText(segments);
  // A PDF page or a PowerPoint shape that cannot carry a hyperlink still has to
  // say where the licence lives, so the plain form prints the deed URL.
  const plainText = license && license.url ? `${text} ${license.url}` : text;

  return { segments, text, plainText, license };
};

/**
 * Everything the note under a figure displays: `segments` for a renderer that
 * can draw italics and a link, `text` for one that cannot, `plainText` for one
 * that also cannot link (the licence URL is spelled out).
 *
 * Returns null for built-in clipart, which needs no attribution at all.
 */
export const buildFigureNote = (meta = {}) => {
  if (meta.sourceType === 'clipart_builtin') return null;
  return buildNote(meta, { includeTitle: true });
};

/**
 * The note under a table. Same shape, minus the title: a table's number and
 * title are drawn above it as their own lines (see `formatTableLabel`).
 */
export const buildTableNote = (meta = {}) => {
  if (meta.sourceType !== 'table') return null;
  return buildNote(meta, { includeTitle: false });
};

export const formatFigureNote = (meta = {}) => {
  const note = buildFigureNote(meta);
  return note ? note.text : null;
};

export const formatTableNote = (meta = {}) => {
  const note = buildTableNote(meta);
  return note ? note.text : null;
};

const FIGURE_SOURCE_TYPES = new Set(['image', 'clipart_external']);

/**
 * The note for a figure or a table, whichever the source is — and null for
 * anything else, because a book, a webpage or a video cites itself with a
 * parenthetical in-text citation rather than a note.
 */
export const buildMediaNote = (meta = {}) => {
  if (meta.sourceType === 'table') return buildTableNote(meta);
  if (FIGURE_SOURCE_TYPES.has(meta.sourceType)) return buildFigureNote(meta);
  return null;
};
